import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTracking } from 'react-tracking'
import { passwordValidator } from 'utils'
import { SignUpAnalytics } from 'constants/'

type dispatchEventData = {
    fieldName?: string
    errorMessage?: string
    password_is_valid?: boolean
    funnelCurrentStep?: string
    passwordAttributes?: string
    itemText?: string
    has_mdm?: string
    password?: string
    passwordStrength?: string
    passwordHidden?: boolean
    name?: string
    pathname?: string
}

type event = 'confirmCpf'

type eventsType =
    | 'personalDataOnOpen'
    | 'personalDataOnFieldBlur'
    | 'passwordOnOpen'
    | 'passwordOnFieldBlur'
    | 'setPhoneOnOpen'
    | 'setPhoneOnFieldBlur'
    | 'selectContactOnOpen'
    | 'confirmTokenOnOpen'
    | 'confirmTokenOnOpenResent'
    | 'confirmTokenOnVerifyToken'
    | 'signUpSuccess'

export const useSignUpAnaytics = ({
    hasMDM = false,
    shouldCall = true,
    defaultDispatchEvent,
}: { hasMDM?: boolean; shouldCall?: boolean; defaultDispatchEvent?: eventsType } = {}) => {
    const { trackEvent } = useTracking()
    const [wasCalled, setWasCalled] = useState<boolean>(false)
    const memoredShouldCall = useMemo(() => shouldCall, [shouldCall])

    const passwordAttributes = ({
        password,
        passwordStrength,
        passwordHidden,
    }: {
        password?: string
        passwordStrength?: string
        passwordHidden?: boolean
    }) => {
        const passwordValue = password ?? ''
        const { upperValid, lowerValid, numberValid, specialCharValid, isSequencial } = passwordValidator(passwordValue)
        const attributes = {
            upper: upperValid,
            lower: lowerValid,
            num: numberValid,
            special: specialCharValid,
            seq: !isSequencial,
            len: passwordValue.length,
            str: passwordStrength,
            hidden: passwordHidden,
        }
        return JSON.stringify(attributes).replace(/["{}]/g, '')
    }

    const dispatchAnalyticsEvent = useCallback(
        (event: eventsType, data: dispatchEventData = {}) => {
            const eventKeys = {
                personalDataOnOpen: SignUpAnalytics.default.personalData.onOpen,
                personalDataOnFieldBlur: SignUpAnalytics.default.personalData.onFieldBlur(data),
                passwordOnOpen: SignUpAnalytics.default.password.onOpen(data.name),
                passwordOnFieldBlur: SignUpAnalytics.default.password.onFieldBlur({
                    ...data,
                    passwordAttributes: passwordAttributes(data),
                }),
                setPhoneOnOpen: SignUpAnalytics.default.setPhone.onOpen,
                setPhoneOnFieldBlur: SignUpAnalytics.default.setPhone.onFieldBlur(data),
                confirmTokenOnOpen: SignUpAnalytics.default.confirmToken.onOpen,
                confirmTokenOnOpenResent: SignUpAnalytics.default.confirmToken.onOpenResent(data),
                confirmTokenOnVerifyToken: SignUpAnalytics.default.confirmToken.onVerifyToken(data),
                selectContactOnOpen: SignUpAnalytics.default.selectContact,
                signUpSuccess: SignUpAnalytics.default.success,
            }

            const eventToDispatch = { ...eventKeys[event], user: { has_mdm: hasMDM } }

            // Set MDM key on event
            if ('has_mdm' in eventToDispatch.user) {
                eventToDispatch.user.has_mdm = hasMDM
            }

            return trackEvent(eventToDispatch)
        },
        [hasMDM, trackEvent],
    )

    const dispatchAnalyticsEventFeedBack = useCallback(
        (event: event, data: dispatchEventData = {}) => {
            const eventKeys = {
                confirmCpf: SignUpAnalytics.default.confirmCpf(data),
            }

            const eventToDispatch = { ...eventKeys[event] }

            return trackEvent(eventToDispatch)
        },
        [hasMDM, trackEvent],
    )

    useEffect(() => {
        if (defaultDispatchEvent && memoredShouldCall && !wasCalled) {
            setWasCalled(true)
            dispatchAnalyticsEvent(defaultDispatchEvent)
        }
    }, [memoredShouldCall, wasCalled, dispatchAnalyticsEvent, defaultDispatchEvent])

    return { trackEvent, dispatchAnalyticsEvent, dispatchAnalyticsEventFeedBack }
}
